import React, { useState, useEffect } from "react";
import styled from "styled-components";
import colorSet from "@/src/styles/color";
import zIndex from "@/src/styles/zIndex";
import Icon from "@/src/components/atom/Icon";
import CloseSvg from "@/src/assets/icons/icon-close-black.svg";
import DefaultUserSvg from "@/src/assets/icons/icon-default-user.svg";
import CopySvg from "@/src/assets/icons/profile-icon-copy.svg";
import ClockSvg from "@/src/assets/icons/icon-clock.svg";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

interface UserMetaData {
  contact?: string;
  email?: string;
  profileName?: string;
  timezone?: string;
}

interface UserProfileCardProps {
  visible: boolean;
  onClose: () => void;
  user: {
    userId: string;
    nickname: string;
    profileUrl?: string;
    connectionStatus?: string;
    lastSeenAt?: number;
    createdAt?: number;
    metaData?: UserMetaData;
  } | null;
  position?: { x: number; y: number };
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  visible,
  onClose,
  user,
  position,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<string>("");

  useEffect(() => {
    if (visible && user) {
      const updateTime = () => {
        const userTimezone = user.metaData?.timezone;

        try {
          // Use user's timezone if valid, otherwise use local time.
          // Invalid timezone strings will throw an error, handled in the catch block.
          const time = userTimezone ? dayjs().tz(userTimezone) : dayjs();
          setCurrentTime(time.format("h:mm A"));
        } catch (error) {
          // Log the error and fallback to local time.
          console.error(`Invalid timezone: ${userTimezone}. Falling back to local time.`, error);
          setCurrentTime(dayjs().format("h:mm A"));
        }
      };

      updateTime();

      // 1분마다 시간 업데이트
      const interval = setInterval(updateTime, 60000);

      return () => clearInterval(interval);
    }
  }, [visible, user]);

  if (!visible || !user) return null;

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const isOnline = user.connectionStatus === "online";
  const displayName = user.metaData?.profileName || user.nickname;

  return (
    <>
      <Backdrop onClick={onClose} />
      <CardContainer
        style={
          position
            ? {
                position: "fixed",
                left: `${position.x}px`,
                top: `${position.y + 200}px`,
                transform: "translate(-50%, -100%)",
              }
            : undefined
        }
      >
        <CloseButton onClick={onClose}>
          <Icon iconSrc={CloseSvg} iconSize={24} />
        </CloseButton>

        <ContentWrapper>
          <TopSection>
            <ProfileSection>
              {user.profileUrl &&
              !user.profileUrl.includes(
                "static.sendbird.com/sample/profiles/profile_placeholder.png",
              ) ? (
                <ProfileImage src={user.profileUrl} alt={user.nickname} />
              ) : (
                <DefaultProfileWrapper>
                  <Icon iconSrc={DefaultUserSvg} iconSize={45.71} />
                </DefaultProfileWrapper>
              )}
              <UserName>{displayName}</UserName>
            </ProfileSection>

            {/* <StatusContainer>
              <StatusDotWrapper>
                <StatusDot isOnline={isOnline} />
              </StatusDotWrapper>
              <StatusText>{isOnline ? "Online" : "Offline"}</StatusText>
            </StatusContainer> */}

            <TimeSection>
              <Icon iconSrc={ClockSvg} iconSize={16} />
              <TimeText>{currentTime}</TimeText>
            </TimeSection>
          </TopSection>

          <Divider />

          <InfoSection>
            <InfoRow>
              <InfoLeft>
                <Label>E-mail</Label>
                <Value>{user.metaData?.email || "-"}</Value>
              </InfoLeft>
              {user.metaData?.email && (
                <CopyButton
                  onClick={() =>
                    handleCopy(user.metaData?.email || "", "email")
                  }
                >
                  <Icon iconSrc={CopySvg} iconSize={22} />
                  {copiedField === "email" && (
                    <CopiedTooltip>복사되었습니다.</CopiedTooltip>
                  )}
                </CopyButton>
              )}
            </InfoRow>

            <InfoRow>
              <InfoLeft>
                <Label>Contact</Label>
                <Value>{user.metaData?.contact || "-"}</Value>
              </InfoLeft>
              {user.metaData?.contact && (
                <CopyButton
                  onClick={() =>
                    handleCopy(user.metaData?.contact || "", "contact")
                  }
                >
                  <Icon iconSrc={CopySvg} iconSize={22} />
                  {copiedField === "contact" && (
                    <CopiedTooltip>복사되었습니다.</CopiedTooltip>
                  )}
                </CopyButton>
              )}
            </InfoRow>
          </InfoSection>
        </ContentWrapper>
      </CardContainer>
    </>
  );
};

export default UserProfileCard;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${zIndex.floatingChatUserCard};
`;

const CardContainer = styled.div`
  position: fixed;
  background: ${colorSet.white};
  border-radius: 16px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.1);
  width: 320px;
  padding: 28px 8px 16px;
  z-index: ${zIndex.floatingChatUserCard + 1};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f1f4f6;
  }

  &:active {
    background-color: #e7edf0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  align-items: flex-start;
  padding: 12px 16px;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
`;

const DefaultProfileWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${colorSet.gray7};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UserName = styled.h3`
  color: var(--Gray-Gray-2, #222729);
  font-family: Pretendard;
  font-size: 19px;
  font-style: normal;
  font-weight: 700;
  line-height: 26px;
  margin: 0;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
`;

const StatusDotWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
`;

const StatusDot = styled.div<{ isOnline: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 6px;
  background-color: ${(props) => (props.isOnline ? "#259C72" : colorSet.gray6)};
`;

const StatusText = styled.span`
  font-family: "Pretendard", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: ${colorSet.gray2};
`;

const TimeSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
`;

const TimeText = styled.span`
  font-family: "Pretendard", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: ${colorSet.gray2};
`;

const Divider = styled.div`
  width: 272px;
  height: 1px;
  background-color: ${colorSet.gray9};
  margin: 12px 16px 0 16px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0 16px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: ${colorSet.gray9};
  }

  &:last-child {
    padding-bottom: 16px;
  }

  position: relative;
`;

const Label = styled.label`
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  color: ${colorSet.gray6};
`;

const Value = styled.span`
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${colorSet.gray2};
`;

const InfoLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CopyButton = styled.button`
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: ${colorSet.gray10};
  }
`;

const CopiedTooltip = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #4a5256;
  color: white;
  padding: 8px 20px 8px 16px;
  border-radius: 8px;
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
  z-index: ${zIndex.floatingChatUserCard + 100};
`;
